'use client'
import {
  useState,
  createContext,
  useMemo,
  use,
  useRef,
  useEffect,
  type ReactNode,
  type RefObject,
} from 'react'
import { type DynamicDialogComponent } from '@/features/shared/state/Dialog/types'
import {
  DialogRetailerContextProvider,
  type RetailerContextType,
} from '@/features/shared/context/RetailerContext'
import { useSetAtom } from 'jotai'
import { dialogAtom, initialDialogValue } from '@/features/shared/state/Dialog/atom'
import { type Content } from '@/features/cms/components/CMS/types'
import { CMSContextProvider } from '@/features/cms/components/CMS/CMSContext'

type DialogContentState = {
  component: DynamicDialogComponent | null
  props:
    | (Record<string, unknown> & {
        dialogRetailerContext: RetailerContextType | undefined
        cmsData: Content | null | undefined
      })
    | undefined
  dialogKey?: number
}

const InitialDialogState: DialogContentState = {
  component: null,
  props: { dialogRetailerContext: undefined, cmsData: undefined },
  dialogKey: 0,
}

export const DialogContainer = ({ children }: { children: ReactNode }) => {
  const setAtom = useSetAtom(dialogAtom)
  const [dialogState, setDialogState] = useState(InitialDialogState)

  const { dialogRetailerContext, cmsData, ...restStateData } = dialogState.props ?? {}
  // this is to get the current dialog content state without
  // the need to re-render the component due to state changes
  const getDialogState = useRef(() => dialogState)

  const ModalComponent = dialogState.component

  const value = useMemo((): Omit<DialogContextType, 'getDialogState'> => {
    const onSetDialogState = (state: DialogContentState) => {
      const newState = {
        ...state,
        dialogKey: (getDialogState.current().dialogKey ?? 0) + 1,
      }
      setDialogState(newState)
      getDialogState.current = () => newState
    }
    return {
      setDialogState: onSetDialogState,
      closeDialog: () => {
        onSetDialogState(InitialDialogState)
        setAtom(initialDialogValue)
      },
    }
  }, [setAtom])
  const contextValue: DialogContextType = useMemo(() => ({ ...value, getDialogState }), [value])

  useEffect(() => {
    window.onpopstate = () => {
      value.closeDialog()
    }
  }, [value])

  return (
    <DialogContext value={contextValue}>
      <DialogStateContext value={dialogState}>
        {ModalComponent ? (
          <DialogRetailerContextProvider value={dialogRetailerContext}>
            <DialogCMSContextProvider cmsData={cmsData}>
              <ModalComponent
                {...restStateData}
                onClose={value.closeDialog}
                key={dialogState.dialogKey}
              />
            </DialogCMSContextProvider>
          </DialogRetailerContextProvider>
        ) : null}
        {children}
      </DialogStateContext>
    </DialogContext>
  )
}

const DialogCMSContextProvider = ({
  cmsData,
  children,
}: {
  cmsData: Content | null | undefined
  children: ReactNode
}) => (cmsData ? <CMSContextProvider data={cmsData}>{children}</CMSContextProvider> : children)

type DialogContextType = {
  getDialogState: RefObject<() => DialogContentState>
  setDialogState: (params: DialogContentState) => void
  closeDialog: () => void
}
export const DialogContext = createContext<DialogContextType | null>(null)
export const DialogStateContext = createContext<DialogContentState | null>(null)

export const useDialogContext = () => {
  const context = use(DialogContext)
  if (!context) {
    throw new Error('useDialogContext must be used within a DialogContextProvider')
  }
  return context
}

// the difference between useDialogContext and useDialogStateContext is that
// the context value is reactive to the dialog state changes where as the other one is not.
// therefore, only use this hook if you need the consumer to react to dialog state changes
export const useDialogStateContext = () => {
  const context = use(DialogStateContext)
  if (!context) {
    throw new Error('useDialogStateContext must be used within a DialogContextProvider')
  }
  return context
}
